/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';
import { useI18n } from '@/context/I18nContext';
import PlotlyFigure from '@/components/charts/PlotlyFigure';
import {
    DEFAULT_TIME_SERIES_PREVIEW_LIMIT,
    formatTimeSeriesInterval,
    getParsedTimeSeriesIntervalMs,
    getParsedTimeSeriesPreviewRows,
    getParsedTimeSeriesRowCount,
} from '@/lib/timeSeries/parsedTimeSeries';
import {
    createTimeSeriesPlotlyFigure,
    type TimeSeriesPlotType,
} from '@/lib/timeSeries/timeSeriesPlotly';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

interface TimeSeriesPreviewProps {
    preview: ParsedTimeSeriesCsv;
}

function TimeSeriesPreview({ preview }: TimeSeriesPreviewProps) {
    const { locale, t } = useI18n();
    const [chartType, setChartType] = useState<TimeSeriesPlotType>('line');
    const plotlyFigure = createTimeSeriesPlotlyFigure(preview, {
        chartType,
    });
    const rowCount = getParsedTimeSeriesRowCount(preview);
    const previewRows = getParsedTimeSeriesPreviewRows(
        preview,
        DEFAULT_TIME_SERIES_PREVIEW_LIMIT,
    );
    const timeInterval = formatTimeSeriesInterval(
        getParsedTimeSeriesIntervalMs(preview),
        locale,
    );

    return (
        <div>
            <section className="dashboard-panel mb-4 flex items-stretch justify-start p-5">
                <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-4">
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Parsed rows')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">{rowCount}</dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Preview rows shown')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">{previewRows.length}</dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Parsed columns')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">{preview.columns.length}</dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Time interval')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">{timeInterval}</dd>
                    </div>
                </dl>
            </section>

            <div className="mb-4">
                <label className="mb-2 block text-sm font-semibold">{t('Time series chart output')}</label>
                <div className="mb-3 inline-flex flex-wrap items-center gap-3">
                    <label className="mb-0 text-sm font-semibold" htmlFor="time-series-chart-type">
                        {t('Chart type')}
                    </label>
                    <select
                        id="time-series-chart-type"
                        className="select select-bordered w-auto"
                        value={chartType}
                        onChange={(event) => {
                            setChartType(event.target.value as TimeSeriesPlotType);
                        }}
                    >
                        <option value="line">{t('Line Chart')}</option>
                        <option value="groupedBar">{t('Grouped Bar Chart')}</option>
                        <option value="stackedBar">{t('Stacked Bar Chart')}</option>
                    </select>
                </div>
                {plotlyFigure ? (
                    <PlotlyFigure figure={plotlyFigure} />
                ) : (
                    <div className="card border border-base-300 bg-base-200 shadow-sm">
                        <div className="card-body text-base-content/70">
                            {t('No valid time series chart data available.')}
                        </div>
                    </div>
                )}
            </div>

            <div className="mb-3 text-base-content/70">
                {t(
                    'Showing only the first {limit} parsed rows to keep browser rendering lightweight.',
                    { limit: DEFAULT_TIME_SERIES_PREVIEW_LIMIT },
                )}
            </div>

            <div className="timeseries-preview-table overflow-auto">
                <table className="table table-zebra">
                    <thead>
                        <tr>
                            {preview.columns.map((column) => (
                                <th key={column || '__empty'} scope="col">
                                    {column || '-'}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {previewRows.map((row, rowIndex) => (
                            <tr key={`${rowIndex}-${row['local server time'] ?? 'row'}`}>
                                {preview.columns.map((column) => (
                                    <td
                                        key={`${rowIndex}-${column || '__empty'}`}
                                        className="status-code"
                                    >
                                        {row[column] || ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default TimeSeriesPreview;
